import { useCallback, useEffect, useRef, useState } from 'react';
import { apiRequest } from '../components/utils/api';

export type VerificationStatus =
  | 'not_started'
  | 'processing'
  | 'verified'
  | 'requires_input'
  | 'failed'
  | 'canceled';

interface StatusResponse {
  status: VerificationStatus;
  verifiedAt: string | null;
  lastUpdatedAt: string | null;
}

interface SessionResponse {
  url?: string;
  clientSecret?: string;
  verificationSessionId: string;
  status: VerificationStatus;
}

export function useIdentityVerification(enabled = true) {
  const [status, setStatus] = useState<VerificationStatus>('not_started');
  const [verifiedAt, setVerifiedAt] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isStarting, setIsStarting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const pollTimer = useRef<number | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      setIsLoading(true);
      const data: StatusResponse = await apiRequest('/api/identity-verification/status');
      setStatus(data.status);
      setVerifiedAt(data.verifiedAt);
      setError(null);
    } catch (e: any) {
      setError(e?.error || 'Failed to load verification status');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    fetchStatus();
  }, [enabled, fetchStatus]);

  // Poll while processing.
  useEffect(() => {
    if (status !== 'processing') {
      if (pollTimer.current) {
        window.clearTimeout(pollTimer.current);
        pollTimer.current = null;
      }
      return;
    }
    pollTimer.current = window.setTimeout(fetchStatus, 5000);
    return () => {
      if (pollTimer.current) window.clearTimeout(pollTimer.current);
    };
  }, [status, fetchStatus]);

  const startVerification = useCallback(async () => {
    try {
      setIsStarting(true);
      setError(null);
      const data: SessionResponse = await apiRequest('/api/identity-verification/session', {
        method: 'POST',
        body: { return_url: window.location.href },
      });
      setStatus('processing');
      if (data.url) {
        // Hosted Stripe Identity flow — redirect top-level so the return_url
        // brings the user back to where they started.
        window.location.assign(data.url);
        return;
      }
    } catch (e: any) {
      setError(e?.error || 'Failed to start verification');
    } finally {
      setIsStarting(false);
    }
  }, []);

  return {
    status,
    verifiedAt,
    isLoading,
    isStarting,
    error,
    startVerification,
    refetch: fetchStatus,
  };
}
